import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Alert } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import './styles.scss';

const MFA_TTL_SECONDS = 5 * 60;

const getApiError = (error) => {
  const status = error?.response?.status;
  const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Request failed';

  if (status === 429) return 'Too many requests. Please wait and retry.';
  if (status === 401) return 'Unauthorized request. Please login again.';
  if (status === 400) return message || 'Invalid request.';

  return message;
};

const formatCountdown = (seconds) => {
  const m = Math.floor(Math.max(0, seconds) / 60)
    .toString()
    .padStart(2, '0');
  const s = Math.floor(Math.max(0, seconds) % 60)
    .toString()
    .padStart(2, '0');
  return `${m}:${s}`;
};

const parseRoles = (roles) => {
  if (!Array.isArray(roles)) return { primaryRole: '', roleTitles: '' };
  const parsed = roles
    .map((role) => {
      if (typeof role === 'string') return role;
      if (role?.title) return role.title;
      if (role?.name) return role.name;
      return '';
    })
    .filter(Boolean);

  // Backward compatibility for existing frontend checks that expect 'admin'.
  if (parsed.includes('super_admin') && !parsed.includes('admin')) {
    parsed.push('admin');
  }

  const roleTitles = parsed.join(',');
  const primaryRole = parsed.includes('support')
    ? 'support'
    : parsed.includes('partner-admin')
      ? 'partner-admin'
      : parsed.includes('manager')
        ? 'manager'
        : parsed.includes('admin')
          ? 'admin'
          : parsed[0] || '';

  return { primaryRole, roleTitles };
};

function PartnerPortal() {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  const [mfaQrImage, setMfaQrImage] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'error', message: '' });
  const [now, setNow] = useState(Date.now());

  const mfaRemainingSeconds = useMemo(() => {
    if (!expiresAt) return 0;
    return Math.max(0, Math.floor((expiresAt - now) / 1000));
  }, [expiresAt, now]);

  const isMfaStep = Boolean(mfaToken && expiresAt && mfaRemainingSeconds > 0);

  useEffect(() => {
    const userIsLoggedIn = localStorage.getItem('adminAuthToken') !== null;
    if (!userIsLoggedIn) return;

    if (localStorage.getItem('role') === 'support') {
      navigate('/app/supports');
      return;
    }
    navigate('/app/leads');
  }, [navigate]);

  useEffect(() => {
    if (!isMfaStep) return undefined;
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [isMfaStep]);

  useEffect(() => {
    if (expiresAt && mfaRemainingSeconds === 0) {
      setMfaToken('');
      setMfaSetupRequired(false);
      setMfaQrImage('');
      setExpiresAt(null);
      setOtp('');
      setSnack({ open: true, severity: 'error', message: 'MFA session expired. Please login again.' });
    }
  }, [expiresAt, mfaRemainingSeconds]);

  const showError = (message) => {
    setSnack({ open: true, severity: 'error', message });
  };

  const clearPersistedAuth = () => {
    localStorage.removeItem('adminAuthToken');
    localStorage.removeItem('loginEmail');
    localStorage.removeItem('fname');
    localStorage.removeItem('lname');
    localStorage.removeItem('profileDetails');
    localStorage.removeItem('role');
    localStorage.removeItem('roleTitles');
  };

  const persistAuth = (payload) => {
    const { primaryRole, roleTitles } = parseRoles(payload.roles);
    localStorage.setItem('adminAuthToken', payload.token || '');
    localStorage.setItem('loginEmail', payload.email || '');
    localStorage.setItem('fname', payload.fname || '');
    localStorage.setItem('lname', payload.lname || '');
    localStorage.setItem('profileDetails', JSON.stringify(payload || {}));
    localStorage.setItem('role', primaryRole);
    localStorage.setItem('roleTitles', roleTitles);
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();

    if (!email.trim() || !password.trim()) {
      showError('Email and password are required.');
      return;
    }

    // Ensure stale token cannot bypass fresh MFA login flow.
    clearPersistedAuth();

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_KEY}users/login`, {
        email: email.trim(),
        password
      });

      const body = response?.data || {};
      const payload = body?.data && typeof body.data === 'object' ? body.data : body;
      const mfaRequired = Boolean(
        body?.mfa_required ||
        body?.mfaRequired ||
        payload?.mfa_required ||
        payload?.mfaRequired
      );
      const mfaSetup = Boolean(
        body?.mfa_setup_required ||
        body?.mfaSetupRequired ||
        payload?.mfa_setup_required ||
        payload?.mfaSetupRequired
      );
      const preAuthMfaToken =
        body?.mfa_token ||
        body?.mfaToken ||
        payload?.mfa_token ||
        payload?.mfaToken;
      const qrImage = body?.qrImage || payload?.qrImage || '';

      // If backend provides a pre-auth MFA token, always enter MFA step.
      if (preAuthMfaToken) {
        if (mfaSetup && !qrImage) {
          showError('2FA setup is required, but login response is missing QR image.');
          return;
        }

        setMfaToken(preAuthMfaToken);
        setMfaSetupRequired(mfaSetup);
        setMfaQrImage(qrImage);
        setExpiresAt(Date.now() + MFA_TTL_SECONDS * 1000);
        setOtp('');
        setSnack({ open: false, severity: 'error', message: '' });
        return;
      }

      if (mfaRequired && !preAuthMfaToken) {
        showError('2FA is required, but login response is missing mfa_token.');
        return;
      }

      // Accept both { data: { token... } } and { token... } response styles.
      const finalAuthPayload = body?.data?.token ? body.data : payload?.token ? payload : null;
      if (finalAuthPayload?.token) {
        persistAuth(finalAuthPayload);
        navigate('/app/leads');
        return;
      }

      showError('Invalid login response from server.');
    } catch (error) {
      showError(getApiError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();

    if (!/^\d{6}$/.test(otp)) {
      showError('Enter a valid 6-digit authenticator code.');
      return;
    }
    if (!mfaToken) {
      showError('MFA session missing. Please login again.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_KEY}api/2fa/login-verify`,
        { token: otp },
        { headers: { Authorization: `Bearer ${mfaToken}` } }
      );

      if (!response?.data?.data?.token) {
        showError('Invalid MFA response from server.');
        return;
      }

      persistAuth(response.data.data);
      setMfaToken('');
      setMfaSetupRequired(false);
      setMfaQrImage('');
      setExpiresAt(null);
      setOtp('');
      navigate('/app/leads');
    } catch (error) {
      showError(getApiError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetMfa = () => {
    setMfaToken('');
    setMfaSetupRequired(false);
    setMfaQrImage('');
    setExpiresAt(null);
    setOtp('');
  };

  return (
    <Fragment>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
      >
        <Alert onClose={() => setSnack((prev) => ({ ...prev, open: false }))} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>

      <div className="login-module">
        <Helmet>
          <title>Sequretek | Partner Portal Login | Access Your Account Securely</title>
          <meta httpEquiv="X-Frame-Options" content="DENY" />
          <meta
            name="description"
            content="Login to our Partner Portal to unlock key resources and enhance your collaboration."
          />
        </Helmet>

        <div className="container pe-0 ps-0">
          <div className="login-holder">
            <div className="login-box text-center" style={{ width: '362px' }}>
              <h1 className="login-box-head">Sequretek Partner Portal</h1>
              <h2 className="mt-2 login-box-head1" style={{ height: 42 }}>
                Create leads and deals and explore content
              </h2>

              {!isMfaStep ? (
                <form onSubmit={handlePasswordLogin} className="text-left" style={{ marginTop: 20 }}>
                  <div className="form-group">
                    <input
                      type="email"
                      className="form-control"
                      placeholder="Email *"
                      autoComplete="off"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-group mt-2">
                    <input
                      type="password"
                      className="form-control"
                      placeholder="Password *"
                      autoComplete="off"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isSubmitting}
                    />
                  </div>

                  <button type="submit" disabled={isSubmitting} className="button-color form-control mt-3">
                    {isSubmitting ? 'Signing in...' : 'Continue'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleMfaVerify} className="text-left" style={{ marginTop: 20 }}>
                  <h3 className="text-center" style={{ fontSize: 18 }}>
                    {mfaSetupRequired ? 'Set up two-factor authentication' : 'Two-factor verification'}
                  </h3>
                  <p className="text-center mb-3" style={{ fontSize: 14, color: '#555' }}>
                    {mfaSetupRequired
                      ? 'Scan this QR in your authenticator app, then enter the 6-digit code.'
                      : 'Enter the 6-digit code from your authenticator app.'}
                    <br />
                    Expires in {formatCountdown(mfaRemainingSeconds)}
                  </p>

                  {mfaSetupRequired && mfaQrImage ? (
                    <div className="text-center mb-3">
                      <img src={mfaQrImage} alt="2FA Setup QR" style={{ maxWidth: 220, width: '100%' }} />
                    </div>
                  ) : null}

                  <div className="form-group">
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      className="form-control"
                      placeholder="6-digit code"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      disabled={isSubmitting}
                    />
                  </div>

                  <button type="submit" disabled={isSubmitting || otp.length !== 6} className="button-color form-control mt-3">
                    {isSubmitting ? 'Verifying...' : 'Verify'}
                  </button>

                  <button type="button" onClick={resetMfa} disabled={isSubmitting} className="form-control mt-2">
                    Back to login
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default PartnerPortal;
